import { getIconAsBgImage, getLabelValue } from '@sportal365/smp-widget-utils';
import { EventTypeCode, IMatchEventSnakeCase } from '../../../../../packages/types/football';
import { useContext } from 'react';
import { ConfigContext } from '@velinsi/smp-widgets-ui';
import { styled } from '../../../../../packages/theme/stitches.config';
import { alignByTeamPosition, positionImageByTeamPosition } from '../../helpers/timeline';

type TimelineEventInfoProps = {
  event: IMatchEventSnakeCase;
  customStyles: React.CSSProperties;
};

export const TimelineEventInfo = ({ event, customStyles }: TimelineEventInfoProps) => {
  const containerStyles = {
    ...customStyles,
    display: 'flex',
    alignItems: event?.secondary_player?.name ? 'flex-start' : 'center',
  };
  const { dataLabels, dataTheme } = useContext(ConfigContext);
  const placeholderIcon = dataTheme === 'dark' ? 'participant-placeholder-dark' : 'participant-placeholder';

  const substituteImage =
    event?.type_code === EventTypeCode.SUBSTITUTION
      ? event?.secondary_player?.assets?.thumb?.url
      : event?.primary_player?.assets?.thumb?.url;
  const ImageContainer = (
    <ImageWrapper
      style={{
        backgroundImage: substituteImage ? `url('${substituteImage}')` : `url(${getIconAsBgImage(placeholderIcon)}`,
        ...positionImageByTeamPosition(event),
        minWidth: '32px',
      }}
    ></ImageWrapper>
  );

  switch (event?.type_code) {
    case EventTypeCode.SUBSTITUTION:
      return (
        <div style={containerStyles}>
          {ImageContainer}
          <div>
            <PrimaryInfo style={{ textAlign: alignByTeamPosition(event) }}> {event?.secondary_player?.name} </PrimaryInfo>
            <SecondaryInfo style={{ textAlign: alignByTeamPosition(event) }}>
              {getLabelValue(dataLabels?.on_for, 'on for')} {event?.primary_player?.name}
            </SecondaryInfo>
          </div>
        </div>
      );
    case EventTypeCode.GOAL:
    case EventTypeCode.OWN_GOAL:
    case EventTypeCode.PENALTY_GOAL:
      return (
        <div style={containerStyles}>
          {ImageContainer}
          <div>
            <PrimaryInfo style={{ textAlign: alignByTeamPosition(event) }}>
              {`(${event?.score?.home} - ${event?.score?.away})`} {event?.primary_player?.name}
            </PrimaryInfo>
            {event?.secondary_player && (
              <SecondaryInfo style={{ textAlign: alignByTeamPosition(event) }}>
                {getLabelValue(dataLabels?.assist_by, 'assist by')} {event?.secondary_player?.name}
              </SecondaryInfo>
            )}
          </div>
        </div>
      );
    case EventTypeCode.RED_CARD:
    case EventTypeCode.YELLOW_CARD:
    case EventTypeCode.YELLOW_CARD_RED:
    case EventTypeCode.PENALTY_MISS:
    case EventTypeCode.PENALTY_SHOOTOUT_MISSED:
    case EventTypeCode.PENALTY_SHOOTOUT_SCORED:
      return (
        <div style={containerStyles}>
          {ImageContainer}
          <PrimaryInfo style={{ textAlign: alignByTeamPosition(event) }}> {event?.primary_player?.name} </PrimaryInfo>
        </div>
      );
    default:
      return <div style={containerStyles}></div>;
  }
};

const ImageWrapper = styled.withConfig({ componentId: 'TimelineWidget', displayName: 'ImageWrapper' })('div', {
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  width: '32px',
  height: '32px',
  borderRadius: '4px 4px 0px 0px',
});

export const PrimaryInfo = styled.withConfig({ componentId: 'TimelineWidget', displayName: 'PrimaryInfo' })('div', {
  width: '100%',
  color: '$hiContrast',
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: '600',
  fontFamily: 'var(--fonts-primaryFontFamily)',
});

export const SecondaryInfo = styled.withConfig({ componentId: 'TimelineWidget', displayName: 'SecondaryInfo' })('div', {
  width: '100%',
  color: '$secondaryHeaderTextColor',
  fontSize: '14px',
  lineHeight: '20px',
  fontFamily: 'var(--fonts-primaryFontFamily)',
});

export const GeneralTabInfo = styled.withConfig({ componentId: 'TimelineWidget', displayName: 'GeneralTabInfo' })('div', {
  backgroundColor: 'var(--colors-timelineTabBackgroundColor)',
  color: 'var(--colors-tabTextColor)',
  borderRadius: '8px',
  height: '36px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: '24px',
  fontFamily: 'var(--fonts-primaryFontFamily)',
  fontWeight: '600',
});

export const InjuryTimeInfo = styled.withConfig({ componentId: 'TimelineWidget', displayName: 'InjuryTimeInfo' })('div', {
  height: '20px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: '24px',

  '& > span': {
    display: 'inline-block',
    color: 'var(--colors-secondaryHeaderTextColor)',
    fontFamily: 'var(--fonts-primaryFontFamily)',
    backgroundColor: 'var(--colors-timelineBackgroundColor)',
    border: '1px solid var(--colors-secondaryHeaderTextColor)',
    borderRadius: '12px',
    fontWeight: '600',
    lineHeight: '20px',
    padding: '2px 8px',
  },
});

export const EventInfoWrapper = styled.withConfig({
  componentId: 'TimelineWidget',
  displayName: 'EventInfoWrapper',
})('div', {
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '24px',
  fontFamily: 'var(--fonts-primaryFontFamily)',
});

export const TimelineWidgetWrapper = styled.withConfig({
  componentId: 'TimelineWidget',
  displayName: 'TimelineWidgetWrapper',
})('div', {
  width: '100%',
  color: '$secondaryHeaderTextColor',
  fontSize: '14px',
  lineHeight: '20px',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column-reverse',
  position: 'relative',
  fontFamily: 'var(--fonts-primaryFontFamily)',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: '24px',
    bottom: '32px',
    left: 'calc(50% - 1px)',
    width: '2px',
    backgroundImage:
      'repeating-linear-gradient(to bottom, $timelineLineColor 0, $timelineLineColor 10px, transparent 10px, transparent 20px)',

    zIndex: '1',
    color: '$timelineLineColor',
  },

  '& > *': {
    position: 'relative',
    zIndex: '2',
  },
});

export const TimelineWidgetWrapperEmptyState = styled.withConfig({
  componentId: 'TimelineWidget',
  displayName: 'TimelineWidgetWrapperEmptyState',
})('div', {
  width: '100%',
  color: '$secondaryHeaderTextColor',
  fontSize: '14px',
  lineHeight: '20px',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column-reverse',
  position: 'relative',
  fontFamily: 'var(--fonts-primaryFontFamily)',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: '8px',
    bottom: '8px',
    left: 'calc(50% - 1px)',
    width: '2px',
    backgroundImage:
      'repeating-linear-gradient(to bottom, $timelineLineColor 0, $timelineLineColor 10px, transparent 10px, transparent 20px)',
    zIndex: '1',
    color: '$timelineLineColor',
  },

  '& > *': {
    position: 'relative',
    zIndex: '2',
  },
});

export const NumberCard = styled.withConfig({ componentId: 'TimelineWidget', displayName: 'NumberCard' })('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'absolute',
  left: '50%',
  top: '30%',
  width: '12px',
  height: '14px',
  borderRadius: '2px',
  backgroundColor: 'black',
  color: 'white',
  fontFamily: '$primaryFontFamily',
  fontSize: '$2',
  fontWeight: '500',
});
